// Renders forms from the draft-07 JSON Schemas the server derives from Rust types (schemars):
// objects, numbers, booleans, strings/enums, optional values (anyOf [T, null]), arrays, and
// unions tagged by a constant `kind` (value models). The schema helpers are in lib/schema.ts. Validation is server-side; errors arrive
// as JSON pointers and are shown next to the matching input.

import { Plus, Trash2 } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import { humanize } from '@/lib/format';
import { constOf, constOptions, defaultValue, doc, type JsonSchema, type Obj, optionalInner, resolve, switchBranch, taggedBranches } from '@/lib/schema';

import { Button, Field, Input, Select, Switch, Textarea, cx } from './ui';

export type { JsonSchema } from '@/lib/schema';

type Errors = Record<string, string>;

// ---- inputs --------------------------------------------------------------------------------------

function NumberInput({ value, onChange, integer, invalid }: { value: unknown; onChange: (v: number | undefined) => void; integer?: boolean; invalid?: boolean }) {
	const [text, setText] = useState(value === undefined || value === null ? '' : String(value));
	useEffect(() => {
		if (Number(text) !== value) setText(value === undefined || value === null ? '' : String(value));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);
	return (
		<Input
			inputMode="decimal"
			value={text}
			invalid={invalid}
			onChange={(e) => {
				const t = e.target.value;
				setText(t);
				if (t.trim() === '') return onChange(undefined);
				const v = integer ? Number.parseInt(t, 10) : Number(t);
				if (Number.isFinite(v)) onChange(v);
			}}
		/>
	);
}

function JsonInput({ value, onChange }: { value: unknown; onChange: (v: unknown) => void }) {
	const [text, setText] = useState(JSON.stringify(value ?? null, null, 2));
	const [bad, setBad] = useState(false);
	return (
		<Textarea
			rows={6}
			value={text}
			className={bad ? 'border-danger' : undefined}
			onChange={(e) => {
				setText(e.target.value);
				try {
					onChange(JSON.parse(e.target.value));
					setBad(false);
				} catch {
					setBad(true);
				}
			}}
		/>
	);
}

// ---- renderer ------------------------------------------------------------------------------------

interface NodeProps {
	schema: JsonSchema;
	root: JsonSchema;
	value: unknown;
	onChange: (v: unknown) => void;
	errors: Errors;
	path: string;
	label?: string;
}

const isScalar = (s: JsonSchema, root: JsonSchema) => {
	const r = resolve(s, root);
	const t = Array.isArray(r.type) ? r.type.find((x: string) => x !== 'null') : r.type;
	return ['number', 'integer', 'boolean', 'string'].includes(t) || !!constOptions(r);
};

function errorsUnder(errors: Errors, path: string) {
	return Object.keys(errors).filter((k) => k === path || k.startsWith(`${path}/`));
}

function Node({ schema, root, value, onChange, errors, path, label }: NodeProps) {
	const s = resolve(schema, root);
	const title = s.title ?? (label ? humanize(label) : undefined);
	const error = errors[path];

	const inner = optionalInner(s, root);
	if (inner) {
		const on = value !== undefined && value !== null;
		return (
			<div className={cx('rounded-md border border-border', on ? 'p-3' : 'px-3 py-2')}>
				<div className="flex min-w-0 items-center justify-between gap-3">
					<Switch checked={on} onChange={(v) => onChange(v ? defaultValue(inner, root) : undefined)} label={<span className="font-medium">{title}</span>} />
					{inner.description && !on && <span className="min-w-0 truncate text-right text-[11px] text-fg-muted" title={doc(inner.description)}>{doc(inner.description)}</span>}
				</div>
				{on && (
					<div className="mt-3">
						<Node schema={{ ...inner, title: undefined, description: inner.description }} root={root} value={value} onChange={onChange} errors={errors} path={path} />
					</div>
				)}
				{error && <p className="mt-1 text-xs text-danger">{error}</p>}
			</div>
		);
	}

	const tagged = taggedBranches(s, root);
	if (tagged) {
		const obj = (value ?? {}) as Obj;
		const current = tagged.branches.find((b) => constOf(b.properties?.[tagged.tag]) === obj[tagged.tag]) ?? tagged.branches[0]!;
		const props = { ...current, properties: Object.fromEntries(Object.entries(current.properties ?? {}).filter(([k]) => k !== tagged.tag)) };
		return (
			<div className="flex flex-col gap-3">
				<Field label={title ?? humanize(tagged.tag)} error={errors[`${path}/${tagged.tag}`] ?? error} hint={doc(current.description)}>
					<Select value={String(obj[tagged.tag] ?? '')} onChange={(e) => onChange(switchBranch(obj, tagged.branches.find((b) => constOf(b.properties?.[tagged.tag]) === e.target.value)!, tagged.tag, root))}>
						{tagged.branches.map((b) => {
							const v = constOf(b.properties?.[tagged.tag])!;
							return (
								<option key={v} value={v}>
									{humanize(v)}
								</option>
							);
						})}
					</Select>
				</Field>
				<ObjectBody schema={props} root={root} value={obj} onChange={onChange} errors={errors} path={path} />
			</div>
		);
	}

	const options = constOptions(s);
	if (options) {
		return (
			<Field label={title} error={error} hint={doc(s.description)}>
				<Select value={String(value ?? '')} invalid={!!error} onChange={(e) => onChange(e.target.value)}>
					{options.map((o) => (
						<option key={o.value} value={o.value}>
							{o.label}
						</option>
					))}
				</Select>
			</Field>
		);
	}

	const type = Array.isArray(s.type) ? s.type.find((t: string) => t !== 'null') : s.type;
	switch (type) {
		case 'object':
			if (!s.properties) break;
			return (
				<fieldset className={cx('flex min-w-0 flex-col gap-3', title && 'rounded-md border border-border p-3')}>
					{title && <legend className="px-1 text-sm font-semibold">{title}</legend>}
					{s.description && title && <p className="-mt-1 text-xs text-fg-muted">{doc(s.description)}</p>}
					<ObjectBody schema={s} root={root} value={(value ?? {}) as Obj} onChange={onChange} errors={errors} path={path} />
					{error && <p className="text-xs text-danger">{error}</p>}
				</fieldset>
			);
		case 'number':
		case 'integer':
			return (
				<Field label={title} error={error} hint={doc(s.description)}>
					<NumberInput value={value} integer={type === 'integer'} invalid={!!error} onChange={onChange} />
				</Field>
			);
		case 'boolean':
			return (
				<div className="flex flex-col gap-1 pt-5">
					<Switch checked={!!value} onChange={onChange} label={title} />
					{error && <span className="text-xs text-danger">{error}</span>}
				</div>
			);
		case 'string':
			return (
				<Field label={title} error={error} hint={doc(s.description)}>
					<Input value={String(value ?? '')} invalid={!!error} onChange={(e) => onChange(e.target.value)} />
				</Field>
			);
		case 'array': {
			const items = Array.isArray(value) ? value : [];
			return (
				<div className="flex flex-col gap-2">
					<div className="flex items-center justify-between">
						<span className="text-xs font-medium text-fg-muted">{title}</span>
						<Button size="sm" variant="ghost" onClick={() => onChange([...items, defaultValue(s.items, root)])}>
							<Plus className="size-3.5" /> Add
						</Button>
					</div>
					{items.map((item, i) => (
						<div key={i} className="flex items-start gap-2 rounded-md bg-muted/60 p-2">
							<div className="flex-1">
								<Node schema={s.items} root={root} value={item} errors={errors} path={`${path}/${i}`} onChange={(v) => onChange(items.map((x, j) => (j === i ? v : x)))} />
							</div>
							<Button size="sm" variant="ghost" aria-label="Remove" onClick={() => onChange(items.filter((_, j) => j !== i))}>
								<Trash2 className="size-3.5" />
							</Button>
						</div>
					))}
					{error && <p className="text-xs text-danger">{error}</p>}
				</div>
			);
		}
	}
	return (
		<Field label={title ?? 'Value'} error={error} hint="Raw JSON">
			<JsonInput value={value} onChange={onChange} />
		</Field>
	);
}

function ObjectBody({ schema, root, value, onChange, errors, path }: Omit<NodeProps, 'value' | 'label'> & { value: Obj }) {
	const entries = Object.entries<JsonSchema>(schema.properties ?? {});
	const set = (k: string, v: unknown) => {
		const next = { ...value };
		if (v === undefined) delete next[k];
		else next[k] = v;
		onChange(next);
	};
	const node = ([k, p]: [string, JsonSchema]) => (
		<Node key={k} schema={p} root={root} label={k} value={value[k]} errors={errors} path={`${path}/${k}`} onChange={(v) => set(k, v)} />
	);
	// Schema order is kept; runs of scalar inputs share one responsive grid.
	const nodes: ReactNode[] = [];
	let run: [string, JsonSchema][] = [];
	const flush = () => {
		if (run.length)
			nodes.push(
				<div key={`grid-${run[0]![0]}`} className="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-3">
					{run.map(node)}
				</div>,
			);
		run = [];
	};
	for (const e of entries) {
		if (isScalar(e[1], root)) run.push(e);
		else {
			flush();
			nodes.push(node(e));
		}
	}
	flush();
	return <>{nodes}</>;
}

export function SchemaForm({ schema, value, onChange, errors = {} }: { schema: JsonSchema; value: unknown; onChange: (v: unknown) => void; errors?: Errors }) {
	const unplaced = Object.keys(errors).filter((k) => k === '/' || k === '');
	return (
		<div className="flex flex-col gap-4">
			<Node schema={schema} root={schema} value={value} onChange={onChange} errors={errors} path="" />
			{unplaced.map((k) => (
				<p key={k} className="text-xs text-danger">
					{errors[k]}
				</p>
			))}
		</div>
	);
}

/** Errors that point inside a subtree, e.g. to badge a collapsed section. */
export const countErrors = (errors: Errors, path: string) => errorsUnder(errors, path).length;
